/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React from 'react';
import { Button } from '@ui/atoms/Button';
import { CardFooter } from '../../AIGeneralBlockCard';
import { UndoButton } from '../UndoButton';
import { useAIAgentMessage } from '../../AIAgentMessage/context';
import './BlockWrapperStyles.scss';
const BASE_CLASS = 'qmb-ui-block-wrapper';
// A `query` action asks the next question for the user (FR31: the how-to reply
// and the day brief offer "what next" buttons). Those read as a row of chips
// under the answer, part of the conversation; every other action keeps the card
// toolbar.
function isFollowUp(action) {
    return action.action === 'query';
}
function footerActionVariant(variant) {
    if (variant === 'primary' || variant === 'danger')
        return variant;
    return 'default';
}
/**
 * BlockWrapper Component
 *
 * Wraps block renderers and conditionally renders an UndoButton below the block
 * when `undoData` is present. This avoids modifying every individual block renderer.
 *
 * @example
 * ```tsx
 * <BlockWrapper block={block}>
 *   <MarkdownRenderer block={block} />
 * </BlockWrapper>
 * ```
 *
 * @version 1.0.0
 * @since 2026-03-04
 */
export function BlockWrapper({ block, children }) {
    var _a;
    const { onAction, onUndo } = useAIAgentMessage();
    const allActions = onAction ? (_a = block.footerActions) !== null && _a !== void 0 ? _a : [] : [];
    const followUps = allActions.filter(isFollowUp);
    const footerActions = allActions
        .filter((action) => !isFollowUp(action))
        .map((action) => ({
        id: action.id,
        label: action.label,
        icon: action.icon,
        variant: footerActionVariant(action.variant),
        disabled: action.disabled,
        onClick: () => { var _a; return onAction === null || onAction === void 0 ? void 0 : onAction(action.action, (_a = action.params) !== null && _a !== void 0 ? _a : {}, action.label); }
    }));
    return (React.createElement(React.Fragment, null,
        children,
        footerActions.length > 0 && React.createElement(CardFooter, { groups: [{ actions: footerActions }] }),
        followUps.length > 0 && (React.createElement("div", { className: `${BASE_CLASS}__follow-ups`, role: "group", "aria-label": "Suggested next questions" }, followUps.map((action) => (React.createElement(Button, { key: action.id, variant: "secondary", disabled: action.disabled, onClick: () => { var _a; return onAction === null || onAction === void 0 ? void 0 : onAction(action.action, (_a = action.params) !== null && _a !== void 0 ? _a : {}, action.label); } }, action.label))))),
        block.undoData && onUndo && React.createElement(UndoButton, { undoData: block.undoData, onUndo: onUndo })));
}
